import { alpha, Box, Button, Divider, Link, Stack, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDownwardRounded } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useDestructiveConfirm } from "src/utilities";
import {
  IssueActions,
  IssueContextProperty,
  IssueDecision,
  IssueImpact,
  IssueProperties,
  IssueProposalsList,
  IssueTaskList,
} from ".";
import {
  DrawerLayout,
  IssueFeed,
  ItemDescriptionField,
  ItemDrawerTitle,
  SignalRGroup,
  SnackbarContent,
  useDrawer,
} from "..";
import { Links } from "../../constants";
import { useSession, useSignalR } from "../../hooks";
import {
  queryKeys,
  useDeleteIssue,
  useEditIssue,
  useGetIssueDetail,
  useRemoveIssueUpvote,
  useUpvoteIssue,
} from "../../http";
import { ITeamSummary } from "../../models";

interface IProps {
  issueGuid: string;
}

const IssueDrawerContent = (props: IProps) => {
  const user = useSession();
  const { isLoading, data: issue } = useGetIssueDetail(props.issueGuid);
  const { mutate: editIssue } = useEditIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const { mutate: deleteIssue } = useDeleteIssue();
  const { enqueueSnackbar } = useSnackbar();
  const { closeDrawer } = useDrawer();
  const queryClient = useQueryClient();
  const signalR = useSignalR();
  const destructiveConfirm = useDestructiveConfirm();
  const theme = useTheme();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.Issue(props.issueGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.Issue(props.issueGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.issueGuid]);

  useEffect(() => {
    const handleShouldRefetchIssue = (issueGuid: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(issueGuid));
    };

    signalR.onShouldRefetchIssue(handleShouldRefetchIssue);
    return () => signalR.offShouldRefetchIssue(handleShouldRefetchIssue);
  }, [queryClient, signalR]);

  async function handleDescriptionChanged(value: string) {
    if (issue === undefined) {
      return;
    }

    editIssue({
      issueGuid: issue.guid,
      teamSlug: issue.assignedTeam?.slug,
      description: value,
      createdByUserId: issue.createdByUser.userId,
    });
  }

  async function handleUpvoteClicked() {
    if (issue === undefined) {
      return;
    }

    if (issue.upvotes.findIndex((x) => x.userId === user.userId) > -1) {
      removeIssueUpvote({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
        userId: user.userId,
      });
    } else {
      upvoteIssue({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
        user: {
          userId: user.userId,
          displayName: user.displayName,
          enabled: true,
          profilePhotoSmallUrl: user.profilePhotoSmallUrl,
        },
      });
    }
  }

  function handleTeamChanged(team: ITeamSummary | null) {
    if (issue === undefined) {
      return;
    }

    enqueueSnackbar(
      <SnackbarContent
        title={
          team ? (
            <>
              Loop moved to{" "}
              <Link onClick={(e) => e.stopPropagation()} href={Links.TeamImplementation(team.slug)}>
                {team.name}
              </Link>
              .
            </>
          ) : (
            "Loop removed from team."
          )
        }
      />,
      {
        action: <Button href={Links.IssueDetail(issue.guid)}>Open Loop</Button>,
      },
    );

    closeDrawer();
  }

  async function handleDeleteClicked() {
    if (issue === undefined) {
      return;
    }

    const { confirmed } = await destructiveConfirm({
      title: "Delete Loop",
      content: "Are you sure you want to delete this loop?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    closeDrawer();
    deleteIssue({
      issueGuid: issue.guid,
      createdByUserId: issue.createdByUser.userId,
      teamSlug: issue.assignedTeam?.slug,
    });
  }

  return (
    <DrawerLayout
      title={
        <ItemDrawerTitle
          link={Links.IssueDetail(props.issueGuid)}
          onDeleteClicked={issue?.permissions.userCanDeleteIssue ? handleDeleteClicked : undefined}
        />
      }
      subtitle={
        <>
          <Stack spacing={0.5}>
            <Box>
              {!isLoading && issue && (
                <IssueContextProperty
                  issue={issue}
                  isEditable={issue.permissions.userCanEditContext}
                  openDrawerOnClick
                />
              )}

              <ItemDescriptionField
                isLoading={isLoading}
                isEditable={issue?.permissions.userCanEditDescription ?? false}
                value={issue?.description}
                onChange={handleDescriptionChanged}
                variant="h5"
              />
            </Box>

            <IssueActions isLoading={isLoading} issue={issue} onUpvoteClicked={handleUpvoteClicked} />
          </Stack>
        </>
      }
    >
      <Stack divider={<Divider flexItem />} sx={{ overflow: "auto" }}>
        <Stack
          divider={
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ArrowDownwardRounded sx={{ color: alpha(theme.palette.primary.main, 0.5), fontSize: 20 }} />
            </Box>
          }
          sx={{ p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
        >
          <IssueImpact issue={issue} isLoading={isLoading} />
          <IssueProposalsList issue={issue} isLoading={isLoading} />
          <IssueDecision issue={issue} isLoading={isLoading} />
          <IssueTaskList issueGuid={props.issueGuid} issue={issue} openDrawerOnTaskItemClick />
        </Stack>
        <IssueProperties issue={issue} isLoading={isLoading} onTeamChanged={handleTeamChanged} />
        <IssueFeed
          issueGuid={props.issueGuid}
          issueCreatedByUserGuid={issue?.createdByUser.userId}
          teamSlug={issue?.assignedTeam?.slug}
        />
      </Stack>
    </DrawerLayout>
  );
};

export { IssueDrawerContent };
